"use client";
import React, { useState } from "react";
import { CommentList } from "./CommentList";
import { CommentForm } from "./CommentForm";

export const ReviewItem = ({ review }) => {
  const [votes, setVotes] = useState({
    upvotes: review.upvotes || 0,
    downvotes: review.downvotes || 0,
  });
  const [userVote, setUserVote] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(review.comments || []);

  const handleVote = (voteType) => {
    if (userVote === voteType) {
      // User is removing their vote
      setVotes({
        ...votes,
        [voteType]: votes[voteType] - 1,
      });
      setUserVote(null);
    } else {
      // User is changing their vote or voting for the first time
      const newVotes = { ...votes };

      if (userVote) {
        // Remove previous vote
        newVotes[userVote] = newVotes[userVote] - 1;
      }

      // Add new vote
      newVotes[voteType] = newVotes[voteType] + 1;

      setVotes(newVotes);
      setUserVote(voteType);
    }
  };

  const handleAddComment = (newComment) => {
    const commentWithId = {
      ...newComment,
      id: `comment-${Date.now()}`,
      author: "You", // In a real app, this would be the logged-in user
    };

    setComments([...comments, commentWithId]);
    // Auto-expand comments when a new one is added
    setShowComments(true);
  };

  return (
    <article className="p-4 mb-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
      <div className="flex flex-wrap justify-between mb-2">
        <h3 className="text-lg font-medium text-gray-800">
          {review.channelName}
        </h3>
        <span className="text-sm text-gray-500">
          Reviewed by {review.reviewer}
        </span>
      </div>

      <p className="mb-3 text-gray-700">{review.content}</p>

      <div className="flex items-center gap-2 mb-3">
        <a
          href={review.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 hover:underline text-sm flex items-center"
        >
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
            <path
              fillRule="evenodd"
              d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
              clipRule="evenodd"
            />
          </svg>
          Watch on YouTube
        </a>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            className={`flex items-center gap-1 ${
              userVote === "upvotes" ? "text-green-600" : "text-gray-500"
            } hover:text-green-600`}
            onClick={() => handleVote("upvotes")}
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                clipRule="evenodd"
              />
            </svg>
            <span>{votes.upvotes}</span>
          </button>

          <button
            className={`flex items-center gap-1 ${
              userVote === "downvotes" ? "text-red-600" : "text-gray-500"
            } hover:text-red-600`}
            onClick={() => handleVote("downvotes")}
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            <span>{votes.downvotes}</span>
          </button>

          <button
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700"
            onClick={() => setShowComments(!showComments)}
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                clipRule="evenodd"
              />
            </svg>
            <span>{comments.length}</span>
          </button>
        </div>

        <span className="text-xs text-gray-500">
          {new Date(review.date).toLocaleDateString()}
        </span>
      </div>

      {showComments && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Comments</h4>
          <CommentList comments={comments} />
          <CommentForm onSubmit={handleAddComment} />
        </div>
      )}
    </article>
  );
};
